import { Language } from '@/api/types';

export const PRIMARY_LANGUAGES: Language[] = ['english', 'spanish'];

const BROWSER_PREFIX_TO_LANGUAGE: Record<string, Language> = {
  en: 'english',
  es: 'spanish',
  ru: 'russian',
  uk: 'ukrainian',
};

/**
 * The interface language for a first-time visitor, derived from their browser
 * locale(s). Returns the first supported match from `navigator.languages`
 * (by primary subtag, so `es-MX` → spanish), falling back to English. An
 * explicit stored selection always overrides this.
 */
export const detectBrowserLanguage = (): Language => {
  const tags =
    typeof navigator === 'undefined'
      ? []
      : navigator.languages ?? [navigator.language];
  for (const tag of tags) {
    const prefix = tag?.toLowerCase().split('-')[0];
    const language = prefix ? BROWSER_PREFIX_TO_LANGUAGE[prefix] : undefined;
    if (language) return language;
  }
  return 'english';
};

export const LANGUAGE_LABEL: Record<Language, string> = {
  english: 'English',
  spanish: 'Español',
  russian: 'Русский',
  ukrainian: 'Українська',
};

export const LANGUAGE_SHORT_CODE: Record<Language, string> = {
  english: 'EN',
  spanish: 'ES',
  russian: 'RU',
  ukrainian: 'UA',
};

const LANGUAGE_ORDER: Record<Language, number> = {
  english: 0,
  spanish: 1,
  russian: 3,
  ukrainian: 2,
};

export const normalizeLanguages = (
  languages: ReadonlyArray<Language>
): Language[] => {
  return [...new Set(languages)].sort(
    (left, right) => LANGUAGE_ORDER[left] - LANGUAGE_ORDER[right]
  );
};

export const splitPrimaryAndSecondaryLanguages = (
  languages: ReadonlyArray<Language>
) => {
  const normalized = normalizeLanguages(languages);
  const primary = normalized.filter((lang) => PRIMARY_LANGUAGES.includes(lang));
  const secondary = normalized.filter(
    (lang) => !PRIMARY_LANGUAGES.includes(lang)
  );

  return [primary, secondary] as const;
};

export const getAutoSelectLanguage = ({
  availableLanguages,
  languageFromParams,
}: {
  availableLanguages: ReadonlyArray<Language>;
  languageFromParams?: Language | null;
}) => {
  if (
    languageFromParams &&
    availableLanguages.includes(languageFromParams)
  ) {
    return languageFromParams;
  }

  if (availableLanguages.length === 1) {
    return availableLanguages[0];
  }

  return null;
};
